import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Warning, Package } from "@phosphor-icons/react";
import api, { API, money, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";

export default function PoApproval() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const presetAction = sp.get("action");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {decision}
  const [declining, setDeclining] = useState(presetAction === "decline");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/po-approvals/${token}`);
        setData(res.data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/po-approvals/${token}/decide`, { decision, reason });
      setResult(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden" data-testid="po-approval-page">
        <div className="border-b-4 border-primary px-6 py-4 flex items-center gap-3">
          <img src={`${API}/public/company-logo${data?.tenant_id ? `?t=${data.tenant_id}` : ""}`} alt="" className="h-9 max-w-[160px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="text-lg font-black tracking-tight">Purchase Order Approval</span>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );

  if (loading) return <Shell><div className="py-10 text-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div></Shell>;

  if (error && !data) {
    return <Shell><div className="py-8 text-center" data-testid="po-approval-error"><Warning size={40} className="mx-auto text-amber-500 mb-3" /><p className="text-sm text-zinc-600">{error}</p></div></Shell>;
  }

  if (result) {
    const approved = result.decision === "approve";
    return (
      <Shell>
        <div className="py-6 text-center" data-testid="po-approval-result">
          {approved ? <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-500 mb-3" /> : <XCircle size={52} weight="fill" className="mx-auto text-rose-500 mb-3" />}
          <h2 className="text-xl font-bold">{approved ? "Approved" : "Declined"}</h2>
          <p className="text-sm text-zinc-600 mt-1">Purchase order <strong>{result.doc_no}</strong> has been {approved ? "approved" : "declined"}. The requester has been updated.</p>
        </div>
      </Shell>
    );
  }

  const done = data.already_decided || data.expired || data.superseded;

  return (
    <Shell>
      {done ? (
        <div className="py-6 text-center" data-testid="po-approval-closed">
          <Warning size={40} className="mx-auto text-amber-500 mb-3" />
          <p className="text-sm text-zinc-600">
            {data.expired ? "This approval link has expired." : data.superseded ? "A newer approval request was sent for this purchase order." : `This purchase order is already marked "${data.approval_status}". No action needed.`}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3 mb-4">
            <Package size={22} className="text-primary mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Approval requested{data.submitted_by ? ` by ${data.submitted_by}` : ""}</p>
              <h2 className="text-xl font-black">{data.doc_no}</h2>
              <p className="text-sm text-zinc-600">{data.vendor_name}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm mb-4">
            {data.department && <div><span className="text-zinc-400">Department</span><div className="font-medium">{data.department}</div></div>}
            {data.sales_order_no && <div><span className="text-zinc-400">Sales Order</span><div className="font-medium">{data.sales_order_no}</div></div>}
            <div><span className="text-zinc-400">Date</span><div className="font-medium">{data.date}</div></div>
            <div><span className="text-zinc-400">Total</span><div className="font-bold text-primary">{money(data.total)}</div></div>
          </div>

          <div className="border border-zinc-200 rounded-sm overflow-hidden mb-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-zinc-500 text-xs uppercase"><tr><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Amount</th></tr></thead>
              <tbody>
                {data.lines.map((l, i) => (
                  <tr key={i} className="border-t border-zinc-100"><td className="px-3 py-2">{l.description}</td><td className="px-3 py-2 text-right">{Number(l.quantity)}</td><td className="px-3 py-2 text-right">{money(l.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && <p className="text-sm text-rose-600 mb-3" data-testid="po-approval-action-error">{error}</p>}

          {declining ? (
            <div className="space-y-3" data-testid="po-decline-panel">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason for declining (optional)" className="w-full rounded-sm border border-zinc-300 p-2 text-sm outline-none focus:border-primary resize-none" data-testid="po-decline-reason" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeclining(false)} className="rounded-sm flex-1" data-testid="po-decline-back">Back</Button>
                <Button onClick={() => decide("decline")} disabled={submitting} className="rounded-sm flex-1 bg-rose-600 hover:bg-rose-700 text-white" data-testid="po-confirm-decline">{submitting ? "Submitting…" : "Confirm Decline"}</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button onClick={() => decide("approve")} disabled={submitting} className="rounded-sm flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="po-approve-btn">
                <CheckCircle size={18} weight="bold" className="mr-1.5" /> {submitting ? "Approving…" : "Approve"}
              </Button>
              <Button variant="outline" onClick={() => setDeclining(true)} className="rounded-sm flex-1 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="po-decline-btn">
                <XCircle size={18} weight="bold" className="mr-1.5" /> Decline
              </Button>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
